import { Injectable, inject } from '@angular/core';
import { ILogger, LogContext } from './ilogger';
import { AppInsightsLoggerService } from './app-insights-logger.service';
import { FallbackLoggerService } from './fallback-logger.service';
import { RuntimeConfigService } from '../runtime-config.service';
import { redactSensitiveData } from './log-redaction.util';

@Injectable({
  providedIn: 'root'
})
export class LoggerService implements ILogger {
  private readonly runtimeConfigService = inject(RuntimeConfigService);
  private readonly appInsightsLogger = inject(AppInsightsLoggerService);
  private readonly fallbackLogger = inject(FallbackLoggerService);
  private activeLogger: ILogger = this.fallbackLogger;
  private resolvedAfterRuntimeConfigLoad = false;

  debug(message: string, context?: LogContext): void {
    this.safeInvoke(() => this.resolveLogger().debug(message, this.sanitizeContext(context)));
  }

  info(message: string, context?: LogContext): void {
    this.safeInvoke(() => this.resolveLogger().info(message, this.sanitizeContext(context)));
  }

  warn(message: string, context?: LogContext): void {
    this.safeInvoke(() => this.resolveLogger().warn(message, this.sanitizeContext(context)));
  }

  error(message: string, context?: LogContext): void {
    this.safeInvoke(() => this.resolveLogger().error(message, this.sanitizeContext(context)));
  }

  exception(error: unknown, context?: LogContext): void {
    this.safeInvoke(() => this.resolveLogger().exception(error, this.sanitizeContext(context)));
  }

  private resolveLogger(): ILogger {
    if (this.activeLogger === this.appInsightsLogger) {
      return this.appInsightsLogger;
    }

    // Before runtime config finishes loading, keep using fallback.
    if (!this.runtimeConfigService.isLoaded()) {
      return this.fallbackLogger;
    }

    // Once config is loaded, resolve backend once and cache it.
    if (this.resolvedAfterRuntimeConfigLoad) {
      return this.activeLogger;
    }

    const appInsightsConfig = this.runtimeConfigService.getAppInsightsConfig();

    if (appInsightsConfig.enabled && appInsightsConfig.connectionString) {
      this.appInsightsLogger.initialize(appInsightsConfig.connectionString);
      this.activeLogger = this.appInsightsLogger;
      this.resolvedAfterRuntimeConfigLoad = true;
      return this.appInsightsLogger;
    }

    // If App Insights is not enabled or the connection string is missing, fall back to the console logger.
    this.activeLogger = this.fallbackLogger;
    this.resolvedAfterRuntimeConfigLoad = true;
    return this.fallbackLogger;
  }

  private sanitizeContext(context?: LogContext): LogContext {
    return context ? redactSensitiveData(context) : {};
  }

  // Fail safe method to ensure no unforeseen errors break the app.
  private safeInvoke(handler: () => void): void {
    try {
      handler();
    } catch {
      // Never break the app flow for telemetry failures.
    }
  }
}
